import { useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { cn } from '@/lib/utils';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

interface ArticleContentProps {
  content: string;
  className?: string;
}

/** Turn `nostr:` and bare NIP-19 references into internal links. */
function preprocessNostrLinks(markdown: string): string {
  // Convert `nostr:npub1...` / `nostr:note1...` etc. into markdown links to /<id>
  return markdown.replace(
    /nostr:((?:npub1|nprofile1|note1|nevent1|naddr1)[0-9a-z]+)/g,
    (_match, id: string) => {
      try {
        const decoded = nip19.decode(id);
        let label = id.slice(0, 12) + '…';
        if (decoded.type === 'npub' || decoded.type === 'nprofile') {
          label = '@' + id.slice(0, 10) + '…';
        }
        return `[${label}](/${id})`;
      } catch {
        return id;
      }
    },
  );
}

/**
 * Render a kind 30023 article's markdown content with GFM support including
 * footnotes. Footnote references and their definitions are cross-linked
 * automatically by remark-gfm (`<sup>` anchors <-> footnote list at the bottom),
 * and we style the back-references (↩) so readers can navigate both ways.
 */
export function ArticleContent({ content, className }: ArticleContentProps) {
  const processed = useMemo(() => preprocessNostrLinks(content), [content]);

  const components: Components = useMemo(
    () => ({
      a({ href, children, ...props }) {
        const raw = href ?? '';
        // Internal app links (nostr identifiers / footnote anchors).
        if (raw.startsWith('/') || raw.startsWith('#')) {
          if (raw.startsWith('#')) {
            // Footnote anchors / back-references — keep as in-page anchors.
            return (
              <a href={raw} {...props}>
                {children}
              </a>
            );
          }
          return (
            <Link to={raw} className="text-primary hover:underline">
              {children}
            </Link>
          );
        }
        const safe = sanitizeUrl(raw);
        if (!safe) {
          return <span>{children}</span>;
        }
        return (
          <a
            href={safe}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-primary hover:underline break-words"
            {...props}
          >
            {children}
          </a>
        );
      },
      img({ src, alt }) {
        const safe = typeof src === 'string' ? sanitizeUrl(src) : undefined;
        if (!safe) return null;
        return (
          <img
            src={safe}
            alt={alt ?? ''}
            loading="lazy"
            className="rounded-lg my-6 mx-auto max-h-[70vh] w-auto"
          />
        );
      },
    }),
    [],
  );

  return (
    <div className={cn('article-body', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {processed}
      </ReactMarkdown>
    </div>
  );
}
